import NoData from 'components/elements/Reports/NoData';
import { TableProps } from 'components/structure/Common/Table';
import 'components/structure/Common/Table.scss';
import React, { useState } from 'react';
import { useFilters, useGlobalFilter, usePagination, useTable } from 'react-table';

export function LiveBookingsTable<T extends object>({
  columns,
  data,
  name,
  CreateForm,
  DeleteForm,
  updateQuery,
  deleteQuery,
  editQuery,
  queryName
}: TableProps<T>): React.ReactElement {
  const [updateShowModal, setUpdateShowModal] = useState(false);
  const [deleteShowModal, setDeleteShowModal] = useState(false);
  const [editUUID, setEditUUID] = useState('');
  const [deleteUUID, setDeleteUUID] = useState('');
  const [resourceUUID, setResourceUUID] = useState('');

  const {
    getTableProps,
    getTableBodyProps,
    columns: finalColumns,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable<T>(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
      setEditUUID,
      setDeleteUUID,
      setResourceUUID,
      setUpdateShowModal,
      setDeleteShowModal
    },
    useGlobalFilter,
    useFilters,
    usePagination
  );

  return (
    <>
      {updateShowModal && (
        <CreateForm
          entityName="Update Booking"
          showModal={updateShowModal}
          setShowModal={setUpdateShowModal}
          setEditUUID={setEditUUID}
          editQuery={editQuery}
          editUUID={editUUID}
          updateQuery={updateQuery}
          queryName={queryName}
        />
      )}
      {deleteShowModal && (
        <DeleteForm
          showModal={deleteShowModal}
          setShowModal={setDeleteShowModal}
          setDeleteUUID={setDeleteUUID}
          setResourceUUID={setResourceUUID}
          deleteUUID={deleteUUID}
          resourceUUID={resourceUUID}
          deleteQuery={deleteQuery}
          queryName={queryName}
          entityName={name}
        />
      )}
      <div className="flex justify-between">
        <div className="flex gap-4">
          {finalColumns
            .filter((column) => column.isVisible)
            .map((column) => {
              return column.canFilter ? (
                <React.Fragment key={column.id}>{column.render('Filter')}</React.Fragment>
              ) : null;
            })}
        </div>
      </div>
      <div className="table">
        {!data || !data.length ? (
          <NoData height={300} hint="No ongoing bookings to show" />
        ) : (
          <table {...getTableProps()}>
            <thead className="table-header">
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="table-contents" {...getTableBodyProps()}>
              {
                // Render table rows
                page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                      })}
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        )}
        {/* Pagination */}
        <div className="pagination">
          {data.length > 0 && (
            <>
              <button
                className="w-10 rounded transition-colors duration-300 transform bg-neutral1 sm:inline hover:bg-primary1"
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              >
                {'<<'}
              </button>{' '}
              <button
                className="w-10 rounded transition-colors duration-300 transform bg-neutral1 sm:inline hover:bg-primary1"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                {'<'}
              </button>{' '}
              <button
                className="w-10 rounded transition-colors duration-300 transform bg-neutral1 sm:inline hover:bg-primary1"
                onClick={() => nextPage()}
                disabled={!canNextPage}
              >
                {'>'}
              </button>{' '}
              <button
                className="w-10 rounded transition-colors duration-300 transform bg-neutral1 sm:inline hover:bg-primary1"
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              >
                {'>>'}
              </button>{' '}
              <span>
                Page{' '}
                <strong>
                  {pageIndex + 1} of {pageOptions.length}
                </strong>{' '}
              </span>
              <span>
                | Go to page:{' '}
                <input
                  className="rounded transition-colors duration-300 transform sm:inline hover:bg-primary1"
                  type="number"
                  defaultValue={pageIndex + 1}
                  onChange={(e) => {
                    const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                    gotoPage(pageNumber);
                  }}
                  style={{ width: '75px', padding: '2px 2px 2px 6px' }}
                />
              </span>{' '}
              <select
                className="pt-px pb-[1.5px] rounded transition-colors duration-300 transform bg-neutral1 sm:inline hover:bg-primary1"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
              >
                {[10, 20, 30, 40, 50].map((pgSize) => (
                  <option key={pgSize} value={pgSize}>
                    Show {pgSize}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>
    </>
  );
}
